import { useCallback, useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components'
import { rem } from 'polished'

import CalendarDatePickerSelectBox from './BedbankDatePicker/CalendarDatePickerSelectBox'
import OfferDatePickerSelectBox from '../Filters/DatePicker/OfferDatePickerSelectBox'
import useHolidaySynonymLabels from 'hooks/useHolidaySynonymLabels'

import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferAirportOptions from '../Filters/Airport/OfferAirportOptions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCapacityWithAgesSelection from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import { selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import { isFlashCalendarEnabled } from 'selectors/offerPage/bedbankOfferPageSelectors'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { getCapacitiesForBedbankOffer } from 'lib/offer/offerUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import BedbankPackageSelectionModal from './MobileBookingFlow/Modals/BedbankPackageSelectionModal'
import BedbankRoomAndPackageDetailsModal from './MobileBookingFlow/Modals/BedbankRoomAndPackageDetailsModal'
import BedbankSidebarFilterCalendar from './BedbankSidebarFilterCalendar'

const FiltersBlock = styled.div`
  flex-grow: 1;

  > * {
    height: ${rem(64)};
    width: 100%;
    padding: 0 ${rem(24)};

    &:not(:first-child):not(:last-child) {
      box-shadow: 0 -1px 0px 0px ${props => props.theme.palette.neutral.surface.lightForeground};
    }
  }
`

interface Props {
  activeFilter?: OfferPageFilterType
  offer: App.BedbankOffer
  isCalendarEnabled: boolean
  flightsEnabled: boolean
  filterHistory: Array<OfferPageFilterType>
}

function BedbankSideBarFilters(props: Props) {
  const {
    activeFilter,
    offer,
    isCalendarEnabled,
    flightsEnabled,
    filterHistory,
  } = props

  const intl = useIntl()
  const { yourHoliday } = useHolidaySynonymLabels()
  const dispatch = useOfferPageDispatch()
  const formRef = useRef<HTMLFormElement>(null)
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const hasPreviousFilter = useMemo(() => filterHistory.length > 0, [filterHistory])

  const closeFilter = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.closeFilter, occupancyPicker: true })
      onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    }
  }, [dispatch, onUserAnalyticsAction])

  const filterContinue = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterContinue })
    }
  }, [dispatch])

  const onBack = useCallback(() => {
    if (formRef.current?.reportValidity()) {
      dispatch({ type: OfferPageActions.filterBack })
    }
  }, [dispatch])

  const onApply = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    filterContinue()

    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.Apply)
  }, [onUserAnalyticsAction, filterContinue])

  const onBackHandler = useMemo(() => (
    hasPreviousFilter ? onBack : undefined
  ), [hasPreviousFilter, onBack])

  const capacities = getCapacitiesForBedbankOffer(offer)

  // Workaround for bug in React Modal when quickly switching between open/closed
  // https://github.com/reactjs/react-modal/issues/888
  // https://github.com/reactjs/react-modal/issues/808
  // TODO: Come up with better solution, because this will always delay opening the modal
  const delayedOpen = useDelayedTogglingBoolean(!!activeFilter, { onDelay: 300 })

  return (
    <ModalBase
      isOpen={delayedOpen}
      onClose={closeFilter}
      mode="drawer"
    >
      <form onSubmit={onApply} ref={formRef}>
        {activeFilter === 'all' && <>
          <ModalHeader
            title={yourHoliday}
            onCloseButtonClick={closeFilter}
          />
          <ModalBody>
            <FiltersBlock>
              {isCalendarEnabled ? <CalendarDatePickerSelectBox inSidebar /> : <OfferDatePickerSelectBox inSidebar />}
              <OfferCapacityWithAgesSelectBox inSidebar />
              {flightsEnabled && offer.flights?.flightsEnabled && <OfferAirportSelectBox inSidebar />}
            </FiltersBlock>
          </ModalBody>
        </>}
        {activeFilter === 'airport' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'uizxwnIKlC5sJl0bXqUL',
              defaultMessage: 'Where will you depart from?',
            })}
            subtitle={intl.formatMessage({
              id: '3PQYzbqSLjTJ8fcUNKKR',
              defaultMessage: 'Select your preferred airport',
            })}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBackHandler}
          />
          <ModalBody>
            <ModalContent>
              <OfferAirportOptions />
            </ModalContent>
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: 'zEV-Cf3ihMXXQYkRO2qR',
                defaultMessage: 'Apply',
              }),
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'capacity' && <>
          <ModalHeader
            title={intl.formatMessage({
              id: 'qBJTem6tj7E7j_YlEmlu',
              defaultMessage: 'Who is going?',
            })}
            onCloseButtonClick={closeFilter}
            onBackButtonClick={onBackHandler}
          />
          <ModalBody>
            <OfferCapacityWithAgesSelection capacities={capacities} />
          </ModalBody>
          <ModalFooter
            primaryActionProps={{
              children: intl.formatMessage({
                id: '7OWCfI9fmCxcvOMW9vy_',
                defaultMessage: 'Apply',
              }),
              type: 'submit',
            }}
          />
        </>}
        {activeFilter === 'checkin' && <BedbankSidebarFilterCalendar
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'packageSelection' && <BedbankPackageSelectionModal
          onClose={closeFilter}
          onBack={onBackHandler}
        />}
        {activeFilter === 'roomDetails' && <BedbankRoomAndPackageDetailsModal
          onClose={closeFilter}
        />}
      </form>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Partial<Props>) => ({
  flightsEnabled: selectIsBedbankFlightBookingEnabled(appState),
  isCalendarEnabled: isFlashCalendarEnabled({ ...state, bedbankOffer: ownProps.offer }, appState),
  activeFilter: state.activeFilter,
  filterHistory: state.filterHistory,
}), BedbankSideBarFilters)
